'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: string;
  href?: string;
  onClick?: () => void;
  variant: 'primary' | 'secondary' | 'accent';
}

interface AdminQuickActionsProps {
  onActionClick?: (actionId: string) => void;
  className?: string;
}

const AdminQuickActions = ({ onActionClick, className = '' }: AdminQuickActionsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const quickActions: QuickAction[] = [
    {
      id: 'new-booking',
      label: 'New Booking',
      description: 'Create appointment',
      icon: 'PlusCircleIcon',
      href: '/customer-booking-flow',
      variant: 'primary',
    },
    {
      id: 'add-service',
      label: 'Add Service',
      description: 'Create new service',
      icon: 'BriefcaseIcon',
      href: '/service-management',
      variant: 'secondary',
    },
    {
      id: 'view-today',
      label: "Today's Bookings",
      description: 'View schedule',
      icon: 'CalendarDaysIcon',
      href: '/all-bookings-view',
      variant: 'accent',
    },
    {
      id: 'manage-availability',
      label: 'Set Availability',
      description: 'Update schedule',
      icon: 'ClockIcon',
      href: '/availability-management',
      variant: 'secondary',
    },
  ];

  const handleActionClick = (action: QuickAction) => {
    if (action.onClick) {
      action.onClick();
    }
    onActionClick?.(action.id);
  };

  const getVariantStyles = (variant: string) => {
    switch (variant) {
      case 'primary':
        return 'bg-primary text-primary-foreground hover:bg-primary/90';
      case 'secondary':
        return 'bg-secondary text-secondary-foreground hover:bg-secondary/90';
      case 'accent':
        return 'bg-accent text-accent-foreground hover:bg-accent/90';
      default:
        return 'bg-muted text-foreground hover:bg-muted/80';
    }
  };

  return (
    <div data-component-id="src/components/common/AdminQuickActions.tsx:81:4" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="81" data-component-end-line="183" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className}`}>
      <div data-component-id="src/components/common/AdminQuickActions.tsx:82:6" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="82" data-component-end-line="95" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-6%22%7D" className="flex items-center justify-between mb-6">
        <div data-component-id="src/components/common/AdminQuickActions.tsx:83:8" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="83" data-component-end-line="86" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <h2 data-component-id="src/components/common/AdminQuickActions.tsx:84:10" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="84" data-component-end-line="84" data-component-file="AdminQuickActions.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20font-heading%20font-semibold%20text-foreground%22%2C%22textContent%22%3A%22Quick%20Actions%22%7D" className="text-xl font-heading font-semibold text-foreground">Quick Actions</h2>
          <p data-component-id="src/components/common/AdminQuickActions.tsx:85:10" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="85" data-component-end-line="85" data-component-file="AdminQuickActions.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-caption%20text-muted-foreground%20mt-1%22%2C%22textContent%22%3A%22Common%20tasks%20and%20shortcuts%22%7D" className="text-caption text-muted-foreground mt-1">Common tasks and shortcuts</p>
        </div>

        <button data-component-id="src/components/common/AdminQuickActions.tsx:88:8" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="88" data-component-end-line="94" data-component-file="AdminQuickActions.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22lg%3Ahidden%20p-2%20hover%3Abg-muted%20rounded-md%20transition-smooth%20focus-ring%22%7D"
          onClick={() => setIsExpanded(!isExpanded)}
          className="lg:hidden p-2 hover:bg-muted rounded-md transition-smooth focus-ring"
          aria-label={isExpanded ? 'Collapse actions' : 'Expand actions'}
        >
          <Icon data-component-id="src/components/common/AdminQuickActions.tsx:93:10" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="93" data-component-end-line="93" data-component-file="AdminQuickActions.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={20} />
        </button>
      </div>

      <div data-component-id="src/components/common/AdminQuickActions.tsx:97:6" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="97" data-component-end-line="162" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`
          grid gap-4
          ${isExpanded ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-4'}
          transition-all duration-300
        `}
      >
        {quickActions.map((action) => {
          const content = (
            <>
              <div data-component-id="src/components/common/AdminQuickActions.tsx:107:14" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="107" data-component-end-line="115" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`
                  w-12 h-12 rounded-lg flex items-center justify-center
                  transition-smooth
                  ${getVariantStyles(action.variant)}
                `}
              >
                <Icon data-component-id="src/components/common/AdminQuickActions.tsx:114:16" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="114" data-component-end-line="114" data-component-file="AdminQuickActions.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={action.icon as any} size={24} />
              </div>

              <div data-component-id="src/components/common/AdminQuickActions.tsx:117:14" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="117" data-component-end-line="122" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
                <h3 data-component-id="src/components/common/AdminQuickActions.tsx:118:16" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="118" data-component-end-line="120" data-component-file="AdminQuickActions.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-heading%20font-semibold%20text-foreground%20text-base%22%7D" className="font-heading font-semibold text-foreground text-base">
                  {action.label}
                </h3>
                <p data-component-id="src/components/common/AdminQuickActions.tsx:121:16" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="121" data-component-end-line="121" data-component-file="AdminQuickActions.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-caption%20text-muted-foreground%20mt-1%22%7D" className="text-caption text-muted-foreground mt-1">{action.description}</p>
              </div>

              <Icon data-component-id="src/components/common/AdminQuickActions.tsx:124:14" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="124" data-component-end-line="128" data-component-file="AdminQuickActions.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22text-muted-foreground%20group-hover%3Atext-foreground%20transition-smooth%22%7D"
                name="ArrowRightIcon"
                size={20}
                className="text-muted-foreground group-hover:text-foreground transition-smooth"
              />
            </>
          );

          const baseClasses = `
            group flex items-center gap-4 p-6 rounded-lg
            bg-card border border-border shadow-sm
            hover:shadow-md hover-lift transition-smooth
            focus-ring
          `;

          if (action.href) {
            return (
              <Link data-component-id="src/components/common/AdminQuickActions.tsx:141:14" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="141" data-component-end-line="148" data-component-file="AdminQuickActions.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22%5Bvar%3AbaseClasses%5D%22%7D"
                key={action.id}
                href={action.href}
                className={baseClasses}
                onClick={() => handleActionClick(action)}
              >
                {content}
              </Link>
            );
          }

          return (
            <button data-component-id="src/components/common/AdminQuickActions.tsx:153:12" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="153" data-component-end-line="159" data-component-file="AdminQuickActions.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={action.id}
              onClick={() => handleActionClick(action)}
              className={`${baseClasses} w-full text-left`}
            >
              {content}
            </button>
          );
        })}
      </div>

      <div data-component-id="src/components/common/AdminQuickActions.tsx:164:6" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="164" data-component-end-line="182" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20p-4%20bg-muted%2F50%20rounded-lg%20border%20border-border%22%7D" className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div data-component-id="src/components/common/AdminQuickActions.tsx:165:8" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="165" data-component-end-line="181" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%22%7D" className="flex items-start gap-3">
          <Icon data-component-id="src/components/common/AdminQuickActions.tsx:166:10" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="166" data-component-end-line="170" data-component-file="AdminQuickActions.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22InformationCircleIcon%22%2C%22className%22%3A%22text-primary%20flex-shrink-0%20mt-0.5%22%7D"
            name="InformationCircleIcon"
            size={20}
            className="text-primary flex-shrink-0 mt-0.5"
          />
          <div data-component-id="src/components/common/AdminQuickActions.tsx:171:10" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="171" data-component-end-line="180" data-component-file="AdminQuickActions.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <p data-component-id="src/components/common/AdminQuickActions.tsx:172:12" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="172" data-component-end-line="172" data-component-file="AdminQuickActions.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-body%20font-medium%20text-foreground%22%2C%22textContent%22%3A%22Pro%20Tip%22%7D" className="text-sm font-body font-medium text-foreground">Pro Tip</p>
            <p data-component-id="src/components/common/AdminQuickActions.tsx:173:12" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="173" data-component-end-line="179" data-component-file="AdminQuickActions.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-caption%20text-muted-foreground%20mt-1%22%2C%22textContent%22%3A%22Use%20keyboard%20shortcuts%20for%20faster%20navigation.%20Press%20%20%20%20%20to%20open%20quick%20search.%22%7D" className="text-caption text-muted-foreground mt-1">
              Use keyboard shortcuts for faster navigation. Press{' '}
              <kbd data-component-id="src/components/common/AdminQuickActions.tsx:175:14" data-component-path="src/components/common/AdminQuickActions.tsx" data-component-line="175" data-component-end-line="177" data-component-file="AdminQuickActions.tsx" data-component-name="kbd" data-component-content="%7B%22elementName%22%3A%22kbd%22%2C%22className%22%3A%22px-2%20py-0.5%20text-xs%20font-data%20bg-card%20border%20border-border%20rounded%22%2C%22textContent%22%3A%22Ctrl%20%2B%20K%22%7D" className="px-2 py-0.5 text-xs font-data bg-card border border-border rounded">
                Ctrl + K
              </kbd>{' '}
              to open quick search.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQuickActions;
